import React, { useEffect, useId, useRef } from 'react';

/**
 * Textarea — multi-line text field with the same field chrome as Input.
 *
 * Props:
 *   label, hint, error: strings (optional). When provided, renders the wrapper.
 *   autoGrow: boolean   — grow to fit content (up to maxRows if given)
 *   rows: number        — initial / minimum rows (default 3)
 *   maxRows: number     — cap for autoGrow
 * All native <textarea> props pass through (value, onChange, placeholder…).
 */
const Textarea = React.forwardRef(function Textarea(
  {
    label,
    hint,
    error,
    autoGrow = false,
    rows = 3,
    maxRows,
    id,
    className = '',
    disabled,
    value,
    onChange,
    ...rest
  },
  ref,
) {
  const autoId = useId();
  const inputId = id || autoId;
  const invalid = Boolean(error);
  const innerRef = useRef(null);

  const setRef = (node) => {
    innerRef.current = node;
    if (typeof ref === 'function') ref(node);
    else if (ref) ref.current = node;
  };

  useEffect(() => {
    if (!autoGrow) return;
    const node = innerRef.current;
    if (!node) return;
    node.style.height = 'auto';
    let next = node.scrollHeight;
    if (maxRows) {
      const lh = parseFloat(getComputedStyle(node).lineHeight) || 20;
      const max = lh * maxRows + 16;
      next = Math.min(next, max);
    }
    node.style.height = `${next}px`;
  }, [autoGrow, maxRows, value]);

  const control = (
    <textarea
      ref={setRef}
      id={inputId}
      rows={rows}
      className={`ps-textarea ${className}`.trim()}
      disabled={disabled}
      aria-invalid={invalid || undefined}
      aria-describedby={error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined}
      data-invalid={invalid ? 'true' : undefined}
      value={value}
      onChange={onChange}
      {...rest}
    />
  );

  if (!label && !hint && !error) return control;

  return (
    <div className="ps-field">
      {label && <label className="ps-field__label" htmlFor={inputId}>{label}</label>}
      {control}
      {error ? (
        <span className="ps-field__error" id={`${inputId}-error`}>{error}</span>
      ) : hint ? (
        <span className="ps-field__hint" id={`${inputId}-hint`}>{hint}</span>
      ) : null}
    </div>
  );
});

export default Textarea;
